import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subject, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FileMedia, MediaType } from 'src/app/models/verylys';
import { AbstractService } from '../abstract/abstract.service';
import { VlApiService } from '../vl-api/vl-api.service';
import { reject } from 'lodash';
import { SocketIOService } from '../socketio/socketio.service';

export interface FormatConstraint {
    authorized_mimetypes: string[];
    file_size_min_bytes: number,
    file_size_max_bytes: number,
    width_min: number,
    width_max: number,
    height_min: number,
    height_max: number
}

@Injectable({
    providedIn: 'root'
})

export class FileMediaService extends AbstractService<FileMedia> {
    private cachingUrl: Map<string, SafeResourceUrl | string> = new Map();
    private urlPendingRequests: Map<string, Subject<SafeResourceUrl | string>[]> = new Map();

    protected cacheMethods = {
        get: true,
        getAll: true
    }

    route = "/file-medias/";
    resourceReferenceLabel = "FileMedia";

    constructor(protected vlapi: VlApiService,
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private socketioService: SocketIOService) {
        super(vlapi, socketioService);
    }

    protected crudMethods = {
        create: false,
        update: false,
        delete: false,
        get: false,
        getAll: false
    }

    public getUrlObs(id: string, sanitize: boolean = true): Observable<SafeResourceUrl | string | Blob> {
        if (!id) {
            return of(null);
        }

        if (this.cachingUrl.has(id)) {
            return of(this.cachingUrl.get(id));
        }

        if (!this.urlPendingRequests.has(id)) {
            this.urlPendingRequests.set(id, []);
        }

        const subject$: Subject<SafeResourceUrl | string> = new Subject();
        this.urlPendingRequests.get(id).push(subject$);

        if (this.urlPendingRequests.get(id).length === 1) {
            this.fetchUrl(id);
        }

        return subject$.asObservable();
    }

    private fetchUrl(id: string): void {
        this.http.get(`${environment.api_url}${this.route}${id}/url`, {
            headers: {
                company: environment.company_label
            }
        })
            .subscribe(
                async value => {
                    if (typeof value === 'string') {
                        const stringValue: string = value?.replaceAll(`"`, '');
                        this.cachingUrl.set(id, stringValue);
                        this.completeGetUrlPendingRequests(id, stringValue);
                    } else if (typeof value === 'object') {
                        const data = (value as any)?.data;

                        if (data) {
                            const blob = new Blob([new Uint8Array(data)]);
                            let urlToBlob = window.URL.createObjectURL(blob);
                            this.cachingUrl.set(id, this.sanitizer.bypassSecurityTrustResourceUrl(urlToBlob));
                            this.completeGetUrlPendingRequests(id, this.sanitizer.bypassSecurityTrustResourceUrl(urlToBlob));
                        }
                    } else {
                        this.completeGetUrlPendingRequests(id, null);
                    }
                },
                error => {
                    const subjects = this.urlPendingRequests.get(id);
                    this.urlPendingRequests.delete(id);

                    subjects.forEach(subject => {
                        if (error.status == 301) {
                            subject.error(error);
                        } else {
                            subject.error(error.message ?? error);
                        }
                        subject.complete();
                    });
                }
            );
    }

    private completeGetUrlPendingRequests(id: string, value: SafeResourceUrl | string): void {
        const subjects = this.urlPendingRequests.get(id);
        this.urlPendingRequests.delete(id);

        subjects.forEach(subject => {
            subject.next(value);
            subject.complete();
        });
    }

    /**
     * Get Url of a file media
     * 
     * @param id            Id of the file media
     * @param download      Return as downloadable file
     */
    /*public getUrl(id: string, sanitize: boolean = true): Promise<SafeResourceUrl | string> {
        return new Promise((resolve, reject) => {
            if (!id) {
                return resolve(null);
            }

            this.http.get(`${environment.api_url}${this.route}${id}/url`)
                .subscribe(
                    async value => {
                        if (typeof value === 'string') {
                            return resolve(value?.replaceAll(`"`, ''));
                        }

                        if (typeof value === 'object') {
                            const data = (value as any)?.data;

                            if (data) {
                                const blob = new Blob([new Uint8Array(data)]);
                                let urlToBlob = window.URL.createObjectURL(blob);
                                return resolve(sanitize ? this.sanitizer.bypassSecurityTrustResourceUrl(urlToBlob) : urlToBlob);
                            }
                        }

                        return resolve(null);
                    },
                    error => {
                        if (error.status == 301) {
                            return reject(error);
                        }

                        reject(error.message ?? "Unknown error");
                    }
                )
        });
    }*/

    /**
     * Validate type of file
     * 
     * @param file      File
     * @param mediaType 
     * @returns 
     */
    public validateType(file: File, constraint: FormatConstraint) {
        if (!constraint?.authorized_mimetypes?.includes(file.type)) {
            throw new Error(`File type ${file.type} is not authorized (accepted type: ${constraint?.authorized_mimetypes?.toString()})`);
        }
    }

    /**
     * Validate the format constraint 
     * 
     * @param file          File to be check
     * @param mediaType     Media type to be check
     * @returns 
     */
    public validateFormatConstraint(file: File, constraint: FormatConstraint): Promise<void> {
        return new Promise((resolve, reject) => {
            let type = file.type.split("/")[0];
            if (type != "video" && type != "image") {
                return resolve();
            }

            // check file size min / size max
            let sizeMin = Number(constraint?.file_size_min_bytes);
            let sizeMax = Number(constraint?.file_size_max_bytes);

            if (file.size < sizeMin || file.size > sizeMax) {
                reject(`Minimum file size must be minimum ${sizeMin} and maximum ${sizeMax}`);
            }

            let widthMin = constraint.width_min ?? null;
            let widthMax = constraint.width_max ?? null;
            let heightMin = constraint.height_min ?? null;
            let heightMax = constraint.height_max ?? null;

            if (!widthMin || !widthMax || !heightMin || !heightMax) {
                return resolve();
            }

            let fileReader = new FileReader();
            fileReader.readAsDataURL(file);

            // file reader is ready
            fileReader.onload = (event: any) => {
                // Video checking
                if (type == 'video') {
                    let video = document.createElement('video');
                    video.src = fileReader.result as string;

                    video.preload = 'metadata';

                    // video loaded
                    video.addEventListener('loadedmetadata', () => {
                        let height = video.videoHeight;
                        let width = video.videoWidth;

                        if (width < widthMin || width > widthMax) {
                            reject(`Video width must be minimum ${widthMin} and maximum ${widthMax}`);
                        }

                        if (height < heightMin || height > heightMax) {
                            reject(`Video height must be minimum ${heightMin} and maximum ${heightMax}`);
                        }

                        resolve();
                    });

                    // video load error
                    video.addEventListener('error', () => {
                        reject(`Can not create a video from file`);
                    });
                }

                // image checking
                if (type == 'image') {
                    let image = new Image();
                    image.src = fileReader.result as string;

                    // image loaded
                    image.onload = () => {
                        let height = image.naturalHeight;
                        let width = image.naturalWidth;

                        if (width < widthMin || width > widthMax) {
                            reject(`Image width must be minimum ${widthMin} and maximum ${widthMax}`);
                        }

                        if (height < heightMin || height > heightMax) {
                            reject(`Image height must be minimum ${heightMin} and maximum ${heightMax}`);
                        }

                        resolve();
                    }

                    // image loading error
                    image.onerror = () => {
                        reject(`Can not create an image from file`);
                    }
                }
            };

            // file reader got an error
            fileReader.onerror = () => {
                reject(`Can not read file`);
            }
        });
    }

    public validate(value: FileMedia, ...args: any): void {

    }
}